import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import isEqual from 'fast-deep-equal';
import debounce from 'lodash/debounce';
import { LayoutAtom } from 'Atoms';
import { nonNullable } from 'Utils/functional';

const observerOptions: MutationObserverInit = {
  childList: true,
  subtree: true
};

const SCROLL_HOR = 'scroll-hor';
const SCROLL_HOR_END = 'scroll-hor-end';

const useTableScrollListeners = (tableRef: RefObject<HTMLDivElement | null>): { classList: string[] } => {
  const [layoutInfo] = useAtom(LayoutAtom);
  const [classList, setClassList] = useState<string[]>([]);

  const onHorizontalScroll = useCallback((e: Event) => {
    const target = e.target as HTMLDivElement;
    const table = target.querySelector('table');
    if (!target || !table) return;

    const scrollLeft = target.scrollLeft;
    const containerWidth = target.getBoundingClientRect().width;
    const tableWidth = table.getBoundingClientRect().width;
    const isAbsoluteLeft = scrollLeft === 0;
    const isAbsoluteRight = Math.floor(tableWidth - scrollLeft) <= containerWidth;
    const list: string[] = [isAbsoluteLeft ? null : SCROLL_HOR, isAbsoluteRight ? SCROLL_HOR_END : null].filter(
      nonNullable
    );
    setClassList((curList) => (isEqual(curList, list) ? curList : list));
  }, []);

  const onWindowScroll = useCallback(() => {
    if (!tableRef.current) return;
    const tableHeaderElement = tableRef.current?.querySelector('thead');
    if (!tableHeaderElement) return;
    const tableTopPos = tableRef.current.getBoundingClientRect().top;
    if (tableTopPos < layoutInfo.contentOffsetY) {
      const translatePos = layoutInfo.contentOffsetY - tableTopPos;
      tableHeaderElement.style.transform = `translateY(${translatePos}px)`;
    } else {
      tableHeaderElement.style.removeProperty('transform');
    }
  }, [tableRef, layoutInfo.contentOffsetY]);

  const onResizeDebounce = useMemo(() => {
    return debounce(() => {
      const tableWrapper: HTMLDivElement | null | undefined = tableRef.current?.querySelector('.table-wrapper');
      if (!tableWrapper) return;
      onHorizontalScroll({ target: tableRef.current } as unknown as Event);
      onWindowScroll();
    }, 500);
  }, [tableRef, onHorizontalScroll, onWindowScroll]);

  useEffect(() => {
    if (!tableRef.current) return;
    const tableWrapper = tableRef.current.querySelector('.table-wrapper');
    tableWrapper && tableWrapper.addEventListener('scroll', onHorizontalScroll);
    window.addEventListener('scroll', onWindowScroll);
    window.addEventListener('resize', onResizeDebounce);

    return () => {
      tableWrapper && tableWrapper.removeEventListener('scroll', onHorizontalScroll);
      window.removeEventListener('scroll', onWindowScroll);
      window.removeEventListener('resize', onResizeDebounce);
    };
  }, [tableRef, onResizeDebounce, onHorizontalScroll, onWindowScroll]);

  useEffect(() => {
    const tableWrapper: HTMLDivElement | null | undefined = tableRef.current?.querySelector('.table-wrapper');
    if (!tableWrapper) return;

    const observer: MutationObserver = new MutationObserver(() => {
      onHorizontalScroll({ target: tableWrapper } as unknown as Event);
      onWindowScroll();
    });
    observer.observe(tableWrapper, observerOptions);

    return () => {
      observer.disconnect();
    };
  }, [tableRef, onHorizontalScroll, onWindowScroll]);

  const res = useMemo((): { classList: string[] } => ({ classList }), [classList]);
  return res;
};

export default useTableScrollListeners;
